/**
 * Gift Wrap — enhances snippets/gift-wrap.liquid.
 *
 * The wrap line is the whole state: checking adds the configured
 * variant, unchecking removes it, and the "Gift for" and "Gift message"
 * line properties carry the two fields. Every write goes through
 * @theme/cart-mutations as an intent ("wrapping on with these values" /
 * "off") resolved against a fresh cart read, so rapid toggles converge
 * and a retry can never add a second line.
 *
 * Both fields are required. Partial values are still saved as typed;
 * checkoutBlocked() is what stops checkout while either is empty.
 *
 * The card has three states (data-state): "off", "editing" (fields and
 * a Done button) and "done" (collapsed summary with Edit). The state is
 * kept here and re-applied after every morph.
 *
 * Unsaved drafts live only in memory. Section morphs overwrite the
 * fields; updatedCallback() restores the drafts and caret if newer
 * typing hasn't been saved yet.
 */

import { Component } from '@theme/component';
import { fetchJSON } from '@theme/utilities';
import { cartMutations } from '@theme/cart-mutations';

/** Field name → line property. Order is the order shown on the line. */
const PROPERTIES = {
  recipient: 'Gift for',
  message: 'Gift message',
};
const SAVE_DEBOUNCE_MS = 400;

/** Hidden while gift wrapping is unsaved, failed or incomplete, so express checkout can't skip it. */
const EXPRESS_SELECTOR = '.cart__additional-checkout';

class GiftWrap extends Component {
  #abort = null;
  #timer = null;
  #revision = 0;
  #savedRevision = 0;
  #draft = null;
  #caret = null;
  #wanted = null;
  #failed = false;
  #showRequired = false;
  #state = 'off';
  #op = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('change', this.#onToggle.bind(this), { signal });
    this.addEventListener('input', this.#onInput.bind(this), { signal });
    this.addEventListener('focusout', this.#onBlur.bind(this), { signal });
    this.addEventListener('click', this.#onClick.bind(this), { signal });
    this.addEventListener('keydown', this.#onKeydown.bind(this), { signal });

    this.#state = this.dataset.state || 'off';
    this.hidden = false;
    this.#updateCount();
    this.#syncExpress();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    clearTimeout(this.#timer);
  }

  /** After a morph: keep newer typing, the in-flight toggle intent and shown errors. */
  updatedCallback() {
    super.updatedCallback();
    this.hidden = false;

    if (this.#isDirty) {
      for (const [name, field] of this.#fields) {
        const value = this.#draft[name];
        if (field.value === value) continue;
        field.value = value;
        if (this.#caret?.name === name && document.activeElement === field) {
          field.setSelectionRange(this.#caret.start, this.#caret.end);
        }
      }
    }
    if (this.#wanted !== null && this.#checkbox) {
      this.#checkbox.checked = this.#wanted;
    }
    // The server may have removed the line (e.g. last eligible item gone) or added it.
    if (!this.#checkbox?.checked) this.#state = 'off';
    else if (this.#state === 'off') this.#state = this.dataset.state || 'editing';
    this.#setState(this.#state);
    this.#setError(this.#failed);
    this.#syncRequired();
    this.#updateCount();
    this.#syncExpress();
  }

  /**
   * Called by the cart drawer and cart page before checkout. Shows what's
   * missing and returns true if checkout must not proceed.
   */
  checkoutBlocked() {
    if (this.#failed) return true;
    if (!this.#checkbox?.checked) return false;
    if (!this.#missing().length) return false;

    this.#setState('editing');
    this.#showRequired = true;
    this.#syncRequired();
    this.#fields.get(this.#missing()[0])?.focus();
    return true;
  }

  get #checkbox() {
    return this.querySelector('[data-gift-wrap-toggle]');
  }

  /** @returns {Map<string, HTMLInputElement | HTMLTextAreaElement>} */
  get #fields() {
    return new Map(Array.from(this.querySelectorAll('[data-gift-wrap-field]'), (el) => [el.dataset.giftWrapField, el]));
  }

  get #isDirty() {
    return this.#draft !== null && this.#revision !== this.#savedRevision;
  }

  #onToggle(event) {
    if (!event.target.matches('[data-gift-wrap-toggle]')) return;
    const on = event.target.checked;
    this.#setState(on ? 'editing' : 'off');

    if (!on) {
      // Removing wrapping discards its details so they can't reappear later.
      clearTimeout(this.#timer);
      this.#draft = null;
      this.#savedRevision = this.#revision;
      this.#showRequired = false;
      for (const field of this.#fields.values()) field.value = '';
      this.#syncRequired();
      this.#updateCount();
    }
    this.#save(on);
  }

  #onInput(event) {
    const field = event.target;
    if (!field.matches('[data-gift-wrap-field]')) return;
    this.#draft = this.#readFields();
    this.#caret = { name: field.dataset.giftWrapField, start: field.selectionStart, end: field.selectionEnd };
    this.#revision++;
    this.#syncRequired();
    this.#updateCount();
    this.#syncExpress();

    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#flushFields(), SAVE_DEBOUNCE_MS);
  }

  /** Blur fires before a checkout or View cart click lands, so the save is queued first. */
  #onBlur(event) {
    if (event.target.matches('[data-gift-wrap-field]') && this.#timer) {
      this.#flushFields();
    }
  }

  #onClick(event) {
    if (event.target.closest('[data-gift-wrap-retry]')) {
      this.#save(this.#checkbox?.checked ?? false);
    } else if (event.target.closest('.gift-wrap__done')) {
      this.#onDone();
    } else if (event.target.closest('[data-gift-wrap-edit]')) {
      this.#setState('editing');
      this.#fields.get('recipient')?.focus();
    }
  }

  /** Enter in the recipient input moves to the message instead of submitting the cart form. */
  #onKeydown(event) {
    if (event.key !== 'Enter' || !event.target.matches('input[data-gift-wrap-field]')) return;
    event.preventDefault();
    this.#fields.get('message')?.focus();
  }

  /** Done collapses the card once both fields are filled; otherwise it shows what's missing. */
  #onDone() {
    const missing = this.#missing();
    if (missing.length) {
      this.#showRequired = true;
      this.#syncRequired();
      this.#fields.get(missing[0])?.focus();
      return;
    }
    if (this.#timer) this.#flushFields();
    this.#setState('done');
    this.querySelector('[data-gift-wrap-edit]')?.focus();
  }

  #setState(state) {
    this.#state = state;
    this.dataset.state = state;
    if (state !== 'done') return;

    // Fill the summary from the fields, which may be newer than the last render.
    const { recipient, message } = this.#currentValues();
    const recipientEl = this.querySelector('[data-gift-wrap-summary-recipient]');
    const messageEl = this.querySelector('[data-gift-wrap-summary-message]');
    if (recipientEl) recipientEl.textContent = recipient;
    if (messageEl) messageEl.textContent = message;
  }

  #flushFields() {
    clearTimeout(this.#timer);
    this.#timer = null;
    if (this.#checkbox?.checked) this.#save(true);
  }

  #save(on) {
    const values = on ? this.#currentValues() : { recipient: '', message: '' };

    for (const field of this.#fields.values()) field.removeAttribute('aria-invalid');
    const tooLong = [...this.#fields].find(([name, field]) => field.maxLength > 0 && values[name].length > field.maxLength);
    if (tooLong) {
      const [, field] = tooLong;
      field.setAttribute('aria-invalid', 'true');
      this.#setError(true, this.dataset.tooLong.replace('[max]', field.maxLength));
      return;
    }

    const revision = this.#revision;
    const op = ++this.#op;
    this.#wanted = on;
    this.#failed = false;
    this.#setError(false);
    this.#announce(this.dataset.saving);
    this.#syncExpress(true);

    cartMutations
      .enqueue(
        // Mark the failure inside the write, so it's set before whenIdle() lets checkout resume.
        () =>
          applyIntent(on, values).catch((err) => {
            this.#failed = true;
            throw err;
          }),
        'gift-wrap'
      )
      .then(() => {
        if (revision === this.#revision) {
          this.#savedRevision = revision;
          this.#draft = null;
        }
        this.#announce(this.dataset.saved);
      })
      .catch(() => {
        this.#failed = true;
        this.#setError(true);
        this.#announce('');
      })
      .finally(() => {
        // Only the latest save clears the intent; an earlier one settling mustn't reveal express checkout.
        if (op === this.#op) this.#wanted = null;
        this.#syncExpress();
      });
  }

  #readFields() {
    const values = {};
    for (const [name, field] of this.#fields) values[name] = field.value;
    return values;
  }

  #currentValues() {
    const raw = this.#draft ?? this.#readFields();
    return { recipient: (raw.recipient ?? '').trim(), message: (raw.message ?? '').trim() };
  }

  /** Field names that are still empty. */
  #missing() {
    const values = this.#readFields();
    return [...this.#fields.keys()].filter((name) => !values[name].trim());
  }

  /** Show a field's required error only after a checkout attempt, and clear it once filled. */
  #syncRequired() {
    const missing = this.#showRequired && this.#checkbox?.checked ? this.#missing() : [];
    for (const [name, field] of this.#fields) {
      const show = missing.includes(name);
      const error = field.closest('.gift-wrap__field')?.querySelector('[data-gift-wrap-required]');
      if (error) error.hidden = !show;
      if (show) field.setAttribute('aria-invalid', 'true');
      else if (!this.#failed) field.removeAttribute('aria-invalid');
    }
  }

  #updateCount() {
    const count = this.querySelector('[data-gift-wrap-count]');
    const message = this.#fields.get('message');
    if (!count || !message) return;
    count.textContent = this.dataset.countTemplate.replace('[count]', message.value.length);
  }

  #setError(show, text) {
    const box = this.querySelector('[data-gift-wrap-error]');
    if (!box) return;
    box.hidden = !show;
    const textEl = box.querySelector('[data-gift-wrap-error-text]');
    if (show && textEl) textEl.textContent = text || this.dataset.error || textEl.textContent;
    const retry = box.querySelector('[data-gift-wrap-retry]');
    if (retry) retry.hidden = Boolean(text);
  }

  #announce(text) {
    const status = this.querySelector('[data-gift-wrap-status]');
    if (status) status.textContent = text || '';
  }

  #syncExpress(pending = false) {
    const incomplete = Boolean(this.#checkbox?.checked) && this.#missing().length > 0;
    const block = pending || incomplete || this.#isDirty || this.#failed || this.#wanted !== null;
    document.querySelectorAll(EXPRESS_SELECTOR).forEach((el) => {
      el.hidden = block;
    });
  }
}

/**
 * Make the cart match the intent. Reads the cart first so the decision
 * uses current line keys, and does nothing if it already matches.
 * @param {boolean} on
 * @param {{recipient: string, message: string}} values
 */
async function applyIntent(on, values) {
  const { productId, variantId } = Theme.giftWrap;
  const cart = await cartMutations.readCart();
  const wraps = cart.items.filter((item) => item.product_id === productId);

  if (!on) {
    if (!wraps.length) return cart;
    return fetchJSON(Theme.routes.cart_update_url, {
      updates: Object.fromEntries(wraps.map((item) => [item.key, 0])),
    });
  }

  // Both keys are always sent: Shopify ignores an empty properties object, so blanks would never clear.
  const properties = {};
  for (const [name, property] of Object.entries(PROPERTIES)) {
    properties[property] = values[name];
  }

  const line = wraps[0];
  if (!line) {
    return fetchJSON(Theme.routes.cart_add_url, { items: [{ id: variantId, quantity: 1, properties }] });
  }
  const unchanged = Object.values(PROPERTIES).every(
    (property) => (line.properties?.[property] || '') === (properties[property] || '')
  );
  if (unchanged) return cart;
  return fetchJSON(Theme.routes.cart_change_url, { id: line.key, quantity: 1, properties });
}

if (!customElements.get('gift-wrap')) {
  customElements.define('gift-wrap', GiftWrap);
}
